/**
 * WebGL2 context manager with context-loss recovery.
 *
 * Handles:
 * - Context creation with deterministic flags
 * - Context-loss detection and recovery
 * - Resize handling
 * - GL state tracking
 */
const DEFAULT_OPTIONS = {
    width: 1024,
    height: 1024,
    // alpha:false is the right default for golden-image determinism (the backing
    // store is opaque, so no compositing variance). Cores that need source
    // transparency (e.g. a drop shadow casting from a silhouette) must opt in
    // with { alpha: true } — with alpha:false the alpha channel is forced to 1.0
    // and any alpha-driven effect silently renders blank.
    preserveDrawingBuffer: true,
    alpha: false,
    antialias: false,
    premultipliedAlpha: false,
};
export class GLContext {
    canvas;
    gl;
    _opts;
    _lost = false;
    _onLostCallbacks = [];
    _onRestoredCallbacks = [];
    constructor(canvas, options = {}) {
        this.canvas = canvas;
        this._opts = { ...DEFAULT_OPTIONS, ...options };
        this.canvas.width = this._opts.width;
        this.canvas.height = this._opts.height;
        const gl = this.canvas.getContext("webgl2", {
            preserveDrawingBuffer: this._opts.preserveDrawingBuffer,
            alpha: this._opts.alpha,
            antialias: this._opts.antialias,
            premultipliedAlpha: this._opts.premultipliedAlpha,
        });
        if (!gl) {
            throw new Error("WebGL2 not supported");
        }
        this.gl = gl;
        this._setupContextLossHandlers();
    }
    get context() {
        return this.gl;
    }
    get options() {
        return { ...this._opts };
    }
    get width() {
        return this.canvas.width;
    }
    get height() {
        return this.canvas.height;
    }
    get isLost() {
        return this._lost;
    }
    onContextLost(cb) {
        this._onLostCallbacks.push(cb);
    }
    onContextRestored(cb) {
        this._onRestoredCallbacks.push(cb);
    }
    resize(width, height) {
        this._opts.width = width;
        this._opts.height = height;
        this.canvas.width = width;
        this.canvas.height = height;
        this.gl.viewport(0, 0, width, height);
    }
    _setupContextLossHandlers() {
        this.canvas.addEventListener("webglcontextlost", (e) => {
            e.preventDefault();
            this._lost = true;
            for (const cb of this._onLostCallbacks)
                cb();
        });
        this.canvas.addEventListener("webglcontextrestored", () => {
            this._lost = false;
            // Re-create context attributes
            const gl = this.canvas.getContext("webgl2", {
                preserveDrawingBuffer: this._opts.preserveDrawingBuffer,
                alpha: this._opts.alpha,
                antialias: this._opts.antialias,
                premultipliedAlpha: this._opts.premultipliedAlpha,
            });
            if (gl) {
                this.gl = gl;
            }
            for (const cb of this._onRestoredCallbacks)
                cb();
        });
    }
    dispose() {
        // Release all GL resources
        const ext = this.gl.getExtension("WEBGL_lose_context");
        if (ext)
            ext.loseContext();
    }
}
//# sourceMappingURL=context.js.map